// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
import React, { useEffect, useState } from "react";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";

/**
 * One plan's coupon count: a large, directly-editable number above a slider.
 *
 * Three ways to set it, all writing through the same clamped `onChange`:
 *   - drag the slider,
 *   - click the number and type,
 *   - the up/down arrows for single steps (press-and-hold repeats via key repeat).
 *
 * Typing commits immediately (the live quote depends on it), while a local draft
 * keeps the box showing exactly what was typed — so clearing it shows an empty
 * field rather than snapping to "0" under the cursor. The draft is dropped on blur.
 */
const CouponCountPicker = ({ id, label, count, max, sliderMax, onChange, disabled = false }) => {
  const [draft, setDraft] = useState(String(count));
  const [isEditing, setIsEditing] = useState(false);

  // Keep the box in sync when the value changes elsewhere (slider, arrows, reset).
  useEffect(() => {
    if (!isEditing) setDraft(String(count));
  }, [count, isEditing]);

  const commitDraft = () => {
    setIsEditing(false);
    setDraft(String(count));
  };

  const step = (delta) => onChange(String(count + delta));

  const arrowClasses = [
    "w-7 h-6 flex items-center justify-center rounded-md border",
    borders.default,
    surfaces.primary,
    textColors.secondary,
    "hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed",
    "focus:outline-none focus:ring-2 focus:ring-pink-400 dark:focus:ring-pink-300",
  ].join(" ");

  return (
    <div className={`rounded-2xl border ${borders.default} ${surfaces.secondary} p-4 space-y-3`}>
      <label htmlFor={`${id}-input`} className={`block ${typography.label} ${textColors.secondary}`}>
        {label}
      </label>

      <div className="flex items-center justify-center gap-3">
        <input
          id={`${id}-input`}
          type="text"
          inputMode="numeric"
          value={isEditing ? draft : String(count)}
          disabled={disabled}
          onFocus={() => setIsEditing(true)}
          onChange={(e) => {
            setIsEditing(true);
            setDraft(e.target.value);
            // Commit as they type so the live quote keeps up.
            onChange(e.target.value);
          }}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              step(1);
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              step(-1);
            }
          }}
          aria-label={`${label} count`}
          className={[
            "w-28 text-center bg-transparent border-0 p-0",
            "font-heading font-bold text-4xl sm:text-5xl",
            textColors.primary,
            "focus:outline-none focus:ring-0",
            "disabled:opacity-50",
          ].join(" ")}
        />

        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => step(1)}
            disabled={disabled || count >= max}
            aria-label={`Increase ${label.toLowerCase()}`}
            className={arrowClasses}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => step(-1)}
            disabled={disabled || count <= 0}
            aria-label={`Decrease ${label.toLowerCase()}`}
            className={arrowClasses}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      <input
        type="range"
        min={0}
        max={sliderMax}
        step={1}
        value={Math.min(count, sliderMax)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label={`${label} slider`}
        className="w-full h-2 cursor-pointer accent-purple-700 dark:accent-pink-500 disabled:cursor-not-allowed"
      />

      <p className={`${typography.caption} ${textColors.muted} text-center`}>
        Slider goes to {sliderMax}; type for up to {max}.
      </p>
    </div>
  );
};

export default CouponCountPicker;
